<template>
  <input
    class="input"
    type="text"
    :value="formattedValue"
    @input="formatInput"
    @keydown="handleKeydown"
    @blur="emitModelValueLazy"
    :step="step"
    :disabled="disabled"
  />
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, watch } from 'vue';

const props = defineProps({
  modelValue: {
    type: [String, Number, null],
    default: '',
  },
  step: {
    type: String,
    default: '0.01',
  },
  lazy: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const rawValue = ref<string>(props.modelValue?.toString() ?? '');

const normalizeRawValue = (value: string): string => {
  const trimmed = value.replace(/[^\d.,-]/g, '');
  const isNegative = trimmed.includes('-');

  const unsigned = trimmed.replace(/-/g, '');
  const [integerRaw = '', ...decimalRawParts] = unsigned.split(/[.,]/);

  const integerPart = integerRaw.replace(/\D/g, '');
  const decimalPart = decimalRawParts.join('').replace(/\D/g, '');
  const normalized =
    decimalRawParts.length > 0 ? `${integerPart}.${decimalPart}` : integerPart;

  return isNegative ? `-${normalized}` : normalized;
};

const formatRawValue = (value: string): string => {
  if (!value) return '';

  const isNegative = value.startsWith('-');
  const unsigned = isNegative ? value.slice(1) : value;
  const [integerPart, decimalPart] = unsigned.split(/[.,]/);
  const formattedInteger = (integerPart || '').replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ' ',
  );

  const formattedUnsigned =
    decimalPart !== undefined
      ? `${formattedInteger},${decimalPart}`
      : formattedInteger;

  return isNegative ? `-${formattedUnsigned}` : formattedUnsigned;
};

const formattedValue = computed(() => {
  return formatRawValue(rawValue.value);
});

const formatInput = (event: Event) => {
  const input = event.target as HTMLInputElement;
  const originalValue = input.value;
  const originalCursor = input.selectionStart || 0;
  const rawBeforeCursor = normalizeRawValue(
    originalValue.slice(0, originalCursor),
  );

  const raw = normalizeRawValue(originalValue);
  rawValue.value = raw;

  if (!props.lazy) {
    emit('update:modelValue', raw);
  }

  nextTick(() => {
    const formatted = formatRawValue(raw);
    const formattedBeforeCursor = formatRawValue(rawBeforeCursor);
    const newCursor = Math.min(formattedBeforeCursor.length, formatted.length);

    input.value = formatted;
    input.setSelectionRange(newCursor, newCursor);
  });
};

const handleKeydown = (event: KeyboardEvent) => {
  const input = event.target as HTMLInputElement;
  if (event.key === 'Backspace') {
    const cursorPos = input.selectionStart ?? 0;
    if (cursorPos === 0) return;

    let i = cursorPos - 1;
    while (i >= 0 && /\D/.test(input.value[i])) i--;

    if (i >= 0) {
      const before = input.value.slice(0, i);
      const after = input.value.slice(cursorPos);
      input.value = before + after;

      input.setSelectionRange(i, i);
      event.preventDefault();

      const customEvent: Event = new Event('input', { bubbles: true });
      Object.defineProperty(customEvent, 'target', {
        value: input,
        writable: false,
      });
      formatInput(customEvent);
    }
  }
};

const emitModelValueLazy = () => {
  if (props.lazy) {
    const value = rawValue.value ? Number(rawValue.value) : '';
    emit('update:modelValue', value);
  }
};

watch(
  () => props.modelValue,
  (newValue) => {
    rawValue.value = newValue?.toString() ?? '';
  },
);
</script>

<style scoped>
.input:disabled {
  color: inherit;
  opacity: 1;
  -webkit-text-fill-color: currentColor;
}
</style>
